import * as Sentry from '@sentry/browser';
import { PAYMENT_TYPES, SIGNUP_EVENTS } from '../utils/common-literals';
import { isCanadaBrand, isEmailValidityCheckEnabledBrand } from './brands';
import { appendRegionPathPrefix, getFirstAndLastNameFromFullName } from './common-helper';
import getSessionInfo from './session-Info';
import { SENTRY_TAG, SENTRY_ERROR_TYPE } from './sentry/constants';
import redact from './sentry/redact';
import { RECAPTCHA_TYPE } from './recaptcha';

function makeRequest({
  url = '', method = 'GET', body, additionalHeaders = {},
}) {
  if (!url) {
    throw new Error('Invalid Url');
  }
  const payload = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...additionalHeaders,
    },
    credentials: 'same-origin',
  };

  if (body) {
    payload.body = JSON.stringify(body);
  }

  return window.fetch(url, payload);
}

function getPaymentPayload({
  paymentType,
  paymentDetails,
  planDetails,
  captchaToken,
  recaptcha = {},
}) {
  return {
    paymentType,
    ...paymentDetails,
    selectedPlans: {
      planId: planDetails.selectedPlanId,
      freeTrialPlanId: planDetails.freeTrial && getSessionInfo()?.selectedPlans?.freeTrialPlanId,
      ...(planDetails.numberOfUsers && { noOfUsers: planDetails.numberOfUsers }),
    },
    freeTrial: planDetails.freeTrial || false,
    captchaToken,
    rVersion: recaptcha?.invisibleCaptchaFailed ? RECAPTCHA_TYPE.VISIBLE : RECAPTCHA_TYPE.INVISIBLE,
  };
}

function getContactPayload({ contact, province = {} }) {
  const { firstName, lastName } = getFirstAndLastNameFromFullName(
    contact.fullName.value,
  );
  const { leadInfo: { authType, authorized } } = getSessionInfo();
  return {
    ...(authorized && { authType }),
    firstName,
    lastName,
    email: contact.email.value,
    phone: contact.phoneNumber?.nationalNumber,
    countryCode: contact.phoneNumber?.countryCode,
    companyName: contact.companyName?.value,
    password: contact.password.value,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...(isCanadaBrand && ({ provinceCode: province.provinceCode })),
  };
}

function getPaymentPayloadForAnsweringServiceSignup({
  paymentType = PAYMENT_TYPES.CC,
  paymentDetails = {},
  planDetails,
  captchaToken,
  recaptcha = {},
}) {
  return {
    paymentType,
    ...paymentDetails,
    selectedPlans: {
      planId: planDetails.selectedPlanId,
      addonIds: [...planDetails.selectedAddons],
      ...(planDetails.selectedIVRId && { ivrId: planDetails.selectedIVRId }),
      freeTrialPlanId: planDetails.freeTrial && getSessionInfo()?.selectedPlans?.freeTrialPlanId,
    },
    freeTrial: planDetails.freeTrial || false,
    captchaToken,
    rVersion: recaptcha?.invisibleCaptchaFailed ? RECAPTCHA_TYPE.VISIBLE : RECAPTCHA_TYPE.INVISIBLE,
  };
}

function handleResponseStatus(response) {
  switch (response.status) {
    case 429:
      return {
        status: false,
        success: false,
        message: SIGNUP_EVENTS.RATELIMIT_EXCEEDED,
        analyticsMessage: 'Too many requests',
      };
    default:
      return response.json();
  }
}

export function makeProcessPaymentRequest({
  payload,
  successCallBack, failureCallBack,
}) {
  makeRequest({
    url: '/payment',
    method: 'POST',
    body: getPaymentPayload(payload),
  })
    .then(response => handleResponseStatus(response))
    .then((data) => {
      if (data && data.status) {
        successCallBack({
          ...data,
          planId: payload.planDetails.selectedPlanId,
          ...(payload.planDetails.numberOfUsers && {
            quantity: payload.planDetails.numberOfUsers,
          }),
        });
      } else {
        Sentry.captureMessage(`Upgrade request failed : ${data?.message}`, {
          level: 'error',
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.PAYMENT_FAILED },
          extra: {
            response: data,
          },
          contexts: {
            Payload: redact(payload),
          },
        });
        failureCallBack(data);
      }
    })
    .catch((e) => {
      Sentry.captureException(e,
        {
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.PAYMENT_FAILED },
          level: 'fatal',
        });
      failureCallBack();
    });
}

export function makeRequestToCheckEmailAvailability({
  email,
  captchaToken,
  successCallBack,
  failureCallBack,
}) {
  const additionalHeaders = isEmailValidityCheckEnabledBrand
    ? { 'X-User-Email': email }
    : {};
  makeRequest({
    url: appendRegionPathPrefix(isEmailValidityCheckEnabledBrand ? '/v1/email' : '/email'),
    method: 'POST',
    body: { captchaToken, email },
    additionalHeaders,
  })
    .then(response => handleResponseStatus(response))
    .then((response) => {
      if (response?.success) {
        successCallBack(response);
      } else {
        Sentry.captureMessage(`Email existence check failed : ${response?.message}`, {
          user: {
            email,
          },
          level: 'error',
          extra: {
            response,
          },
        });
        failureCallBack(response);
      }
    })
    .catch((e) => {
      const err = new Error(`Exception while checking email existence: ${e?.message}`);
      err.name = 'CheckEmailExistenceError';
      err.cause = e;
      Sentry.captureException(err,
        {
          user: {
            email,
          },
          level: 'fatal',
        });
      failureCallBack();
    });
}

export function makeSignupRequest({
  payload,
  successCallBack,
  failureCallBack,
}) {
  const body = {
    leadInfo: getContactPayload(payload),
    paymentRequest: getPaymentPayload(payload),
  };
  makeRequest({
    url: '/signup',
    method: 'POST',
    body,
  })
    .then(response => handleResponseStatus(response))
    .then((data) => {
      if (data && data.status) {
        successCallBack(data);
      } else {
        Sentry.captureMessage(`Paid signup request failed : ${data?.message}`, {
          user: {
            email: payload?.contact?.email?.value,
          },
          level: 'error',
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.PAID_SIGNUP_FAILED },
          extra: {
            response: data,
          },
          contexts: {
            Payload: redact(payload),
          },
        });
        failureCallBack(data);
      }
    })
    .catch((e) => {
      Sentry.captureException(e,
        {
          user: {
            email: payload?.contact?.email?.value,
          },
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.PAID_SIGNUP_FAILED },
          level: 'fatal',
        });
      failureCallBack();
    });
}

export function makeFreeSignupRequest({ payload, successCallBack, failureCallBack }) {
  const body = {
    leadInfo: getContactPayload(payload),
    paymentRequest: getPaymentPayload(payload),
  };
  makeRequest({
    url: '/signup/free',
    method: 'POST',
    body,
  })
    .then(response => handleResponseStatus(response))
    .then((data) => {
      if (data && data.status) {
        successCallBack(data);
      } else {
        Sentry.captureMessage(`Free signup request failed : ${data?.message}`, {
          user: {
            email: payload?.contact?.email?.value,
          },
          level: 'error',
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.FREE_SIGNUP_FAILED },
          extra: {
            response: data,
          },
          contexts: {
            Payload: redact(payload),
          },
        });
        failureCallBack(data);
      }
    })
    .catch((e) => {
      Sentry.captureException(e,
        {
          user: {
            email: payload?.contact?.email?.value,
          },
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.FREE_SIGNUP_FAILED },
          level: 'fatal',
        });
      failureCallBack();
    });
}

export function makeAnsweringServiceSignupRequest({
  payload,
  successCallBack,
  failureCallBack,
}) {
  const body = {
    leadInfo: getContactPayload(payload),
    paymentRequest: getPaymentPayloadForAnsweringServiceSignup(payload),
  };
  makeRequest({
    url: appendRegionPathPrefix('/answeringService/signup'),
    method: 'POST',
    body,
  })
    .then(response => handleResponseStatus(response))
    .then((data) => {
      if (data && data.status) {
        successCallBack(data);
      } else {
        Sentry.captureMessage(`Answering service signup failed : ${data?.message}`, {
          user: {
            email: payload?.contact?.email?.value,
          },
          level: 'error',
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.AS_SIGNUP_FAILED },
          extra: {
            response: data,
          },
          contexts: {
            Payload: redact(payload),
          },
        });
        failureCallBack(data);
      }
    })
    .catch((e) => {
      Sentry.captureException(e,
        {
          user: {
            email: payload?.contact?.email?.value,
          },
          level: 'fatal',
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.AS_SIGNUP_FAILED },
        });
      failureCallBack();
    });
}

export function makeFreeTrialSignupRequest({ payload, successCallBack, failureCallBack }) {
  const body = {
    leadInfo: getContactPayload(payload),
    paymentRequest: getPaymentPayload(payload),
  };
  makeRequest({ url: '/signup/freetrial', method: 'POST', body })
    .then(response => handleResponseStatus(response))
    .then((data) => {
      if (data && data.status) {
        successCallBack(data);
      } else {
        Sentry.captureMessage(`Free trial signup request failed ${data?.message}`, {
          user: {
            email: payload.contact.email.value,
          },
          level: 'error',
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.FREE_TRIAL_SIGNUP_FAILED },
          extra: { response: data },
          contexts: { Payload: redact(payload) },
        });
        failureCallBack(data);
      }
    })
    .catch((e) => {
      Sentry.captureException(e,
        {
          user: {
            email: payload?.contact?.email?.value,
          },
          tags: { [SENTRY_TAG.TYPE]: SENTRY_ERROR_TYPE.FREE_TRIAL_SIGNUP_FAILED },
          level: 'fatal',
        });
      failureCallBack();
    });
}

export function makeBrainTreeTokenRequest({
  captchaToken,
  tokenSuccessCallback,
  tokenFailureCallback,
}) {
  makeRequest({
    url: appendRegionPathPrefix('/client-token'),
    method: 'POST',
    body: { captchaToken },
  })
    .then(response => handleResponseStatus(response))
    .then((response) => {
      if (response?.success) {
        tokenSuccessCallback(response);
      } else {
        Sentry.captureMessage(`BrainTree token request failed : ${response?.message}`, {
          level: 'error',
          extra: { response },
        });
        tokenFailureCallback(response);
      }
    })
    .catch((e) => {
      const err = new Error(`Exception while fetching BrainTree token: ${e?.message}`);
      err.name = 'BrainTreeTokenError';
      err.cause = e;
      Sentry.captureException(err,
        {
          user: {
            email: getSessionInfo()?.leadInfo?.email,
          },
          level: 'fatal',
        });
      tokenFailureCallback();
    });
}

export function sendSignupEvent(payload) {
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      });
      navigator.sendBeacon(appendRegionPathPrefix('/api/analytics'), blob);
    }
  } catch (e) {
    const err = new Error(`Error occurred while sending signup event to /api/analytics: ${e.message}`);
    err.name = 'SendSignupEventError';
    err.cause = e;
    Sentry.captureException(err);
  }
}
